import { useState, useEffect, useCallback } from "react";
import { Lock, Download, ArrowLeft, Send, X, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CAMPAIGN = {
  subject: "Pozvánka od CARSPA — akční detailing za 2 999 Kč",
  heading: "Děkujeme,\nže jste u nás byli.",
  body_html: `<p>Chceme vám poděkovat za předchozí využití našich služeb. Vážíme si vaší důvěry.</p>
<p style="margin-top:14px;">Zveme vás na naši aktuální akci — <strong>kompletní detailing za 2 999 Kč</strong> (běžně 6 500 Kč). V ceně je ruční mytí exteriéru, dekontaminace, jednokrokové rozleštění laku Rupes BigFoot a suché čištění interiéru.</p>
<p style="margin-top:14px;">Akce platí pouze do <strong>31. 12. 2026</strong>. Termíny obsazujeme rychle — rezervujte si svůj ještě dnes.</p>`,
  cta_label: "Rezervovat termín",
  cta_url: "https://carspasale.cz/#form",
};

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showCampaign, setShowCampaign] = useState(false);
  const [campaign, setCampaign] = useState(DEFAULT_CAMPAIGN);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchLeads = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/leads`, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) { localStorage.removeItem("admin_token"); setIsLoggedIn(false); setToken(""); return; }
      const data = await res.json();
      setLeads(data);
      setIsLoggedIn(true);
    } catch { setError("Chyba připojení k serveru"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) fetchLeads(token); }, [token, fetchLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
      if (!res.ok) { setError("Špatné heslo"); return; }
      const data = await res.json();
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
    } catch { setError("Chyba připojení"); }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/api/admin/leads/csv`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "leads.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setError("Export se nezdařil"); }
  };

  const handleLogout = () => { localStorage.removeItem("admin_token"); setToken(""); setIsLoggedIn(false); setLeads([]); };

  const toggleLead = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === leads.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(leads.map(l => l.id)));
  };

  const validEmailsInSelection = () => {
    const targets = selectedIds.size > 0 ? leads.filter(l => selectedIds.has(l.id)) : leads;
    const emails = new Set();
    targets.forEach(l => {
      const e = (l.email || '').trim();
      if (e && e.includes('@') && !e.startsWith('not-provided')) emails.add(e);
    });
    return emails.size;
  };

  const sendCampaign = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API}/api/admin/campaign/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...campaign, lead_ids: Array.from(selectedIds) }),
      });
      const data = await res.json();
      setSendResult(data);
    } catch (e) {
      setSendResult({ error: "Odeslání selhalo" });
    } finally {
      setSending(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={handleLogin} className="w-full max-w-sm">
          <div className="border border-white/10 bg-white/[0.02] p-8">
            <div className="w-12 h-12 mx-auto bg-white/[0.04] rounded-full flex items-center justify-center border border-white/15 mb-5">
              <Lock className="w-4 h-4 text-white/80" />
            </div>
            <h1 className="text-xl font-bold text-white text-center tracking-tighter mb-1">Admin Panel</h1>
            <p className="text-white/40 text-xs text-center mb-6 uppercase tracking-[0.3em]">CARSPA — Správa leadů</p>
            {error && <div className="border border-red-500/30 text-red-400 text-sm p-3 mb-4 text-center" data-testid="login-error">{error}</div>}
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Heslo"
              className="w-full bg-transparent border-b border-white/15 text-white px-1 py-3 text-sm focus:outline-none focus:border-white mb-6"
              data-testid="admin-password-input" autoFocus />
            <button type="submit" className="w-full bg-white text-black font-medium py-3 text-[11px] uppercase tracking-[0.3em] hover:bg-white/85 transition-all" data-testid="admin-login-btn">Přihlásit</button>
          </div>
          <a href="/" className="flex items-center justify-center gap-2 text-white/30 text-xs mt-4 hover:text-white/60 transition-colors">
            <ArrowLeft className="w-3 h-3" /> Zpět na web
          </a>
        </form>
      </div>
    );
  }

  const targetCount = selectedIds.size > 0 ? selectedIds.size : leads.length;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-5 sticky top-0 bg-black z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tighter text-white">CARSPA<span className="text-[#22c55e]">.</span> <span className="text-white/40 font-normal ml-2 text-base">Admin</span></h1>
            <p className="text-white/40 text-[11px] uppercase tracking-[0.25em] mt-1">{leads.length} leadů · {selectedIds.size} vybráno</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowCampaign(true)}
              disabled={leads.length === 0}
              className="flex items-center gap-2 bg-[#22c55e] text-black px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] hover:bg-[#16a34a] transition-all disabled:opacity-30" data-testid="campaign-btn">
              <Send className="w-3.5 h-3.5" /> Poslat pozvánku
            </button>
            <button onClick={handleExport}
              className="flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] hover:border-white transition-all" data-testid="export-csv-btn">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button onClick={handleLogout} className="text-white/30 text-[10px] uppercase tracking-[0.25em] hover:text-white/60 transition-colors" data-testid="admin-logout-btn">Odhlásit</button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? <p className="text-white/40 text-center py-12">Načítání...</p> :
          leads.length === 0 ? <div className="text-center py-16 border border-white/5"><p className="text-white/30 text-sm">Zatím žádné leady</p></div> :
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="leads-table">
              <thead>
                <tr className="border-b border-white/10 text-white/40 text-[10px] uppercase tracking-[0.25em]">
                  <th className="py-3 px-3 font-medium w-8">
                    <input type="checkbox" checked={leads.length > 0 && selectedIds.size === leads.length} onChange={toggleAll} className="accent-[#22c55e] w-4 h-4" data-testid="select-all" />
                  </th>
                  <th className="py-3 px-3 font-medium">Jméno</th>
                  <th className="py-3 px-3 font-medium">Telefon</th>
                  <th className="py-3 px-3 font-medium">Email</th>
                  <th className="py-3 px-3 font-medium">Pobočka</th>
                  <th className="py-3 px-3 font-medium">Vozidlo</th>
                  <th className="py-3 px-3 font-medium">Paket</th>
                  <th className="py-3 px-3 font-medium">Datum</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  const selected = selectedIds.has(lead.id);
                  const validEmail = lead.email && lead.email.includes('@') && !lead.email.startsWith('not-provided');
                  return (
                    <tr key={lead.id} className={`border-b border-white/5 transition-colors ${selected ? 'bg-[#22c55e]/[0.05]' : 'hover:bg-white/[0.02]'}`} data-testid={`lead-row-${lead.id}`}>
                      <td className="py-3 px-3">
                        <input type="checkbox" checked={selected} onChange={() => toggleLead(lead.id)} className="accent-[#22c55e] w-4 h-4" data-testid={`select-lead-${lead.id}`} />
                      </td>
                      <td className="py-3 px-3 text-sm text-white">{lead.name}</td>
                      <td className="py-3 px-3 text-sm"><a href={`tel:${lead.phone}`} className="text-white/70 hover:text-[#22c55e]">{lead.phone}</a></td>
                      <td className="py-3 px-3 text-sm">{validEmail ? <a href={`mailto:${lead.email}`} className="text-white/70 hover:text-[#22c55e]">{lead.email}</a> : <span className="text-white/25 italic">bez emailu</span>}</td>
                      <td className="py-3 px-3 text-sm text-white/60">{lead.branch || '-'}</td>
                      <td className="py-3 px-3 text-sm text-white/60">{lead.vehicle_type || '-'}</td>
                      <td className="py-3 px-3 text-xs text-white/50">{lead.selected_upgrades?.join(', ') || '-'}</td>
                      <td className="py-3 px-3 text-xs text-white/40 whitespace-nowrap">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('cs-CZ') : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        }
      </div>

      {/* Campaign Modal */}
      {showCampaign && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" data-testid="campaign-modal">
          <div className="bg-white text-black max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-black/10 px-7 py-5 sticky top-0 bg-white">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#16a34a] font-semibold">— Kampaň</p>
                <h2 className="text-2xl font-bold tracking-tighter">Poslat pozvánku</h2>
              </div>
              <button onClick={() => { setShowCampaign(false); setSendResult(null); }} className="text-black/40 hover:text-black"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-7 space-y-5">
              <div className="bg-black/5 border-l-2 border-[#22c55e] p-4 text-sm">
                <p className="text-black"><strong>Cílová skupina:</strong> {targetCount === leads.length ? `Všech ${leads.length} leadů` : `${targetCount} vybraných`} · <span className="text-black/60">Doručeno bude ~{validEmailsInSelection()} unikátních emailů</span></p>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-black/55 font-semibold mb-2 block">Předmět</label>
                <input type="text" value={campaign.subject} onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })} className="w-full border-b border-black/15 focus:border-black py-2.5 text-black text-base focus:outline-none" data-testid="campaign-subject" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-black/55 font-semibold mb-2 block">Nadpis v e-mailu</label>
                <textarea value={campaign.heading} onChange={(e) => setCampaign({ ...campaign, heading: e.target.value })} rows={2} className="w-full border-b border-black/15 focus:border-black py-2.5 text-black text-xl font-semibold tracking-tighter focus:outline-none resize-none" data-testid="campaign-heading" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-black/55 font-semibold mb-2 block">Tělo e-mailu (HTML)</label>
                <textarea value={campaign.body_html} onChange={(e) => setCampaign({ ...campaign, body_html: e.target.value })} rows={7} className="w-full border border-black/15 focus:border-black p-3 text-black text-sm focus:outline-none font-mono resize-y" data-testid="campaign-body" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-black/55 font-semibold mb-2 block">CTA tlačítko</label>
                  <input type="text" value={campaign.cta_label} onChange={(e) => setCampaign({ ...campaign, cta_label: e.target.value })} className="w-full border-b border-black/15 focus:border-black py-2.5 text-black text-base focus:outline-none" data-testid="campaign-cta-label" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-black/55 font-semibold mb-2 block">CTA URL</label>
                  <input type="url" value={campaign.cta_url} onChange={(e) => setCampaign({ ...campaign, cta_url: e.target.value })} className="w-full border-b border-black/15 focus:border-black py-2.5 text-black text-base focus:outline-none" data-testid="campaign-cta-url" />
                </div>
              </div>

              {sendResult && (
                <div className={`p-4 border ${sendResult.error ? 'border-red-500 bg-red-50 text-red-700' : 'border-[#22c55e] bg-[#22c55e]/10 text-[#16a34a]'} text-sm flex items-start gap-3`} data-testid="campaign-result">
                  {!sendResult.error && <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                  <div>
                    {sendResult.error ? sendResult.error : (
                      <>
                        <strong>{sendResult.sent} emailů odesláno.</strong>
                        {sendResult.skipped > 0 && <span> · {sendResult.skipped} přeskočeno (bez emailu / duplikát).</span>}
                        {sendResult.failed > 0 && <span className="text-red-600"> · {sendResult.failed} selhalo.</span>}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-black/10 px-7 py-5 flex items-center justify-between sticky bottom-0 bg-white">
              <button onClick={() => { setShowCampaign(false); setSendResult(null); }} className="text-black/55 text-[11px] uppercase tracking-[0.3em] hover:text-black transition-colors">Zavřít</button>
              <button onClick={sendCampaign} disabled={sending} className="bg-[#22c55e] text-black px-8 py-3.5 text-[11px] uppercase tracking-[0.3em] font-medium hover:bg-[#16a34a] transition-all disabled:opacity-50 flex items-center gap-2" data-testid="campaign-send">
                {sending ? "Odesílám..." : <><Send className="w-3.5 h-3.5" /> Odeslat {targetCount === leads.length ? 'všem' : `${targetCount} leadům`}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
